import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/endpoints';
import { PAGINATION } from '@/lib/constants';

/**
 * Max number of dataset pages fetched concurrently in the `fetchAll` path.
 * Bounds parallelism so a very large library can't fire an unbounded burst of
 * requests at the API in one tick.
 */
const FETCH_ALL_CONCURRENCY = 5;

/** Params accepted by the dataset list endpoint. */
export type DatasetListParams = {
  page?: number;
  size?: number;
  source_id?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
};

/** Shape returned by both `useDatasets` and `fetchAllDatasetPages`. */
export type DatasetListResult = {
  items: any[];
  total: number;
  page: number;
  limit: number;
  pages: number;
};

/**
 * Fetch EVERY dataset page and concatenate them in page order.
 *
 * Fetches page 1 to learn the page count, then pages 2..N with a bounded worker
 * pool (`FETCH_ALL_CONCURRENCY`) so a very large library can't fire an unbounded
 * burst of requests in one tick.
 *
 * This is expensive: on a production-sized catalog (~1,100 datasets) it costs
 * three cold catalog reads and ~15s, because the API serializes catalog reads
 * onto a single worker thread — parallelism on the client cannot help. Use it
 * only for genuinely all-rows work (CSV export, the views-wizard dataset
 * picker), never to render a page (#964).
 */
export const fetchAllDatasetPages = async (
  params?: Omit<DatasetListParams, 'page' | 'size'>
): Promise<DatasetListResult> => {
  // Fetch page 1 first to learn how many pages exist.
  const firstResponse = await api.datasets.list({
    ...params,
    page: 1,
    size: PAGINATION.MAX_SIZE,
  });

  const firstPageData = firstResponse.data.data || [];
  const total = firstResponse.data.pagination?.total || firstPageData.length;
  const totalPages = firstResponse.data.pagination?.pages || 1;

  // Single page (or unknown pagination): nothing more to fetch.
  if (totalPages <= 1) {
    return { items: firstPageData, total, page: 1, limit: total, pages: totalPages };
  }

  // Fetch pages 2..totalPages concurrently with a bounded worker pool, storing
  // each page's rows at its page index so the final order is deterministic.
  const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
  const pageResults: any[][] = new Array(totalPages);
  pageResults[0] = firstPageData;

  let cursor = 0;
  const worker = async () => {
    while (cursor < remainingPages.length) {
      const pageNum = remainingPages[cursor++];
      const response = await api.datasets.list({
        ...params,
        page: pageNum,
        size: PAGINATION.MAX_SIZE,
      });
      pageResults[pageNum - 1] = response.data.data || [];
    }
  };

  const poolSize = Math.min(FETCH_ALL_CONCURRENCY, remainingPages.length);
  await Promise.all(Array.from({ length: poolSize }, () => worker()));

  return {
    items: pageResults.flatMap((p) => p ?? []),
    total,
    page: 1,
    limit: total,
    pages: totalPages,
  };
};

/**
 * Hook to fetch a page of datasets.
 *
 * Pass `page`/`size`/`search`/`sort`/`order` for **server-side** pagination,
 * search and sort — the preferred mode (#964): one request per view instead of
 * pulling the whole table.
 *
 * `fetchAll: true` pulls the entire library via `fetchAllDatasetPages`; it
 * remains for the callers that genuinely need every row in memory. See that
 * helper for its cost.
 *
 * `options.enabled` lets a caller defer the fetch (#1026) — `DatasetCombobox`
 * uses it so mounting a form costs nothing until its picker is opened.
 */
export const useDatasets = (
  params?: DatasetListParams & { fetchAll?: boolean },
  options?: { enabled?: boolean }
) => {
  const { fetchAll, ...apiParams } = params || {};

  return useQuery({
    queryKey: ['datasets', params],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<DatasetListResult> => {
      if (fetchAll) {
        return fetchAllDatasetPages(apiParams);
      }

      // Single-page fetch (server-side pagination).
      const response = await api.datasets.list(apiParams);
      return {
        items: response.data.data || [],
        total: response.data.pagination?.total || 0,
        page: response.data.pagination?.page || 1,
        limit: response.data.pagination?.size || apiParams?.size || 20,
        pages: response.data.pagination?.pages || 0,
      };
    },
    // Keep the previous page on screen while the next one loads, so paging does
    // not flash an empty table against a multi-second cold read.
    placeholderData: (previous) => previous,
  });
};

/**
 * Hook to fetch a single dataset by ID.
 *
 * Sends `include_stats=false` (#964): on a production-sized catalog the
 * statistics payload costs a separate ~6.7s cold catalog read, and no component
 * renders it — `row_count` / `size_bytes` come from the dataset row itself.
 */
export const useDataset = (id: string) => {
  return useQuery({
    queryKey: ['datasets', id],
    queryFn: async () => {
      const response = await api.datasets.get(id, { include_stats: false });
      return response.data;
    },
    enabled: !!id,
  });
};

/**
 * Hook to preview dataset data.
 *
 * `options.enabled` lets a caller defer the fetch (#964) — the preview is a
 * live LIMIT scan against the dataset and costs a cold catalog read even when
 * the tab that renders it is closed.
 */
export const useDatasetPreview = (
  id: string,
  limit: number = 100,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['datasets', id, 'preview', limit],
    queryFn: async () => {
      const response = await api.datasets.preview(id, { limit });
      return response.data;
    },
    enabled: !!id && (options?.enabled ?? true),
  });
};

/**
 * Hook to fetch dataset schema.
 *
 * `options.enabled` lets a caller defer the fetch (#964).
 */
export const useDatasetSchema = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['datasets', id, 'schema'],
    queryFn: async () => {
      const response = await api.datasets.schema(id);
      return response.data;
    },
    enabled: !!id && (options?.enabled ?? true),
  });
};

/**
 * Hook to create a new dataset
 */
export const useCreateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await api.datasets.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};

/**
 * Hook to update a dataset
 */
export const useUpdateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.datasets.update(id, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      queryClient.invalidateQueries({ queryKey: ['datasets', variables.id] });
    },
  });
};

/** Parameters for the delete dataset mutation. */
export type DeleteDatasetParams = { id: string; force?: boolean; hard_delete?: boolean };

/**
 * Hook to delete a dataset.
 *
 * Accepts an object with `id` and optional `force` / `hard_delete` flags:
 * - `force` includes `?force=true`, bypassing the backend dependency guard.
 * - `hard_delete` includes `?hard_delete=true`, permanently removing the
 *   registry row AND dropping the physical DuckLake table (#972/#973). The
 *   backend restricts hard delete to admins.
 *
 * Only the truthy flags are sent, so a plain `{ id }` posts no query params
 * (preserving the default soft delete).
 */
export const useDeleteDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, force, hard_delete }: DeleteDatasetParams) => {
      const params: { force?: true; hard_delete?: true } = {};
      if (force) params.force = true;
      if (hard_delete) params.hard_delete = true;
      const response = await api.datasets.delete(
        id,
        Object.keys(params).length ? params : undefined
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};
